import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Lock, Eye, AlertTriangle, Loader2, Building2, Clock, CheckCircle2,
  Shield, FileSpreadsheet, LayoutDashboard, Hash, ArrowLeft
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import FooterBackground from '../components/FooterBackground';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARTIFACT_META = {
  dashboard: {
    icon: LayoutDashboard,
    label: 'Shared Dashboard',
    accent: 'from-emerald-500 to-cyan-500',
  },
  focus_export: {
    icon: FileSpreadsheet,
    label: 'FOCUS 1.1 Export',
    accent: 'from-amber-500 to-orange-500',
  },
};

/**
 * Public read-only viewer for /s/:token share-links.
 *
 * Flow:
 *   1. On mount, GET /api/share/view/:token to fetch metadata.
 *      - Returns 404 if token unknown / 410 if revoked or expired.
 *      - Returns needs_password=true if a password gate is active.
 *   2. If password is required, show a password input.
 *      - On submit, POST /api/share/view/:token/data with the password header.
 *      - Bad password → 403 with friendly inline error.
 *   3. Render the artifact contents.
 */
const SharedViewerPage = () => {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [data, setData] = useState(null);

  // ── Step 1: Load preview ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/share/view/${token}`);
        if (!cancelled) {
          setMeta(r.data);
          // No password required — fetch immediately
          if (!r.data.needs_password) {
            await fetchData('');
          }
        }
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          const detail = err.response?.data?.detail;
          if (status === 410) setError(detail || 'This link has expired or been revoked');
          else if (status === 404) setError('This link does not exist or has been deleted');
          else if (status === 403) setError(detail || 'This link is not valid for your account');
          else setError(detail || 'Could not load shared content');
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Step 2: Fetch artifact data (with optional password) ─────────────────
  const fetchData = async (pwd) => {
    setUnlocking(true);
    setPasswordError(null);
    try {
      const headers = pwd ? { 'X-Share-Password': pwd } : {};
      const r = await axios.post(`${API}/share/view/${token}/data`, {}, { headers });
      setData(r.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setPasswordError('Password required');
      else if (status === 403) setPasswordError('Incorrect password — please try again');
      else if (status === 410) setError(err.response?.data?.detail || 'This link has expired');
      else setPasswordError(err.response?.data?.detail || 'Could not load content');
    } finally {
      setUnlocking(false);
    }
  };

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    fetchData(password.trim());
  };

  // ── Render shell ────────────────────────────────────────────────────────
  const Shell = ({ children, wide = false }) => (
    <div className="min-h-screen relative bg-slate-950 text-white" data-testid="shared-viewer-page">
      <FooterBackground />
      <div className={`relative ${wide ? 'max-w-5xl' : 'max-w-md'} mx-auto px-4 pt-16 pb-12`}>
        {children}
        <div className="text-center mt-8">
          <Link to="/" className="text-xs text-slate-400 hover:text-slate-300 transition-colors flex items-center justify-center gap-1.5">
            <ArrowLeft className="w-3 h-3" />
            CARTIEAI · Financial OS for Cloud
          </Link>
        </div>
      </div>
    </div>
  );

  if (loadingMeta) {
    return (
      <Shell>
        <Card className="bg-slate-900/60 border-white/10 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-3 text-emerald-400 animate-spin" />
            <p className="text-slate-400 text-sm">Loading shared content…</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <Card className="bg-slate-900/60 border-rose-500/30 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-rose-400" />
            <h1 className="text-xl font-bold mb-2"><BrandTitle>Link unavailable</BrandTitle></h1>
            <p className="text-sm text-slate-400 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline" className="border-white/20" data-testid="shared-back-home">
                Back to home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Password gate
  if (meta?.needs_password && !data) {
    return (
      <Shell>
        <Card className="bg-slate-900/60 border-white/10 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex p-3 rounded-2xl bg-amber-500/15 mb-4">
                <Lock className="w-6 h-6 text-amber-400" />
              </div>
              <h1 className="text-xl font-bold mb-2"><BrandTitle>Password required</BrandTitle></h1>
              <p className="text-sm text-slate-400">
                This shared {ARTIFACT_META[meta.artifact_type]?.label.toLowerCase() || 'content'} is password-protected.
              </p>
            </div>
            <form onSubmit={handleUnlock} className="space-y-3">
              <Input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
                placeholder="Enter password"
                autoFocus
                data-testid="shared-password-input"
                disabled={unlocking}
              />
              {passwordError && (
                <div className="text-xs text-rose-300 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {passwordError}
                </div>
              )}
              <Button
                type="submit"
                disabled={unlocking || !password.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="shared-unlock-btn"
              >
                {unlocking ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Unlocking…</> : <><Lock className="w-4 h-4 mr-1.5" />Unlock</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Render artifact content
  const meta_ = ARTIFACT_META[meta.artifact_type] || ARTIFACT_META.dashboard;
  const Icon = meta_.icon;
  const orgName = meta.organization?.name || 'Anonymous Organization';
  const expiresAt = meta.expires_at ? new Date(meta.expires_at) : null;
  const expiresIn = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / (1000 * 60 * 60))) : null;

  return (
    <Shell wide>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${meta_.accent} shadow-lg shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest mb-1">
              SHARED · READ-ONLY
            </Badge>
            <h1 className="text-2xl font-bold truncate">
              {meta.label || meta_.label}
            </h1>
            <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5">
              <Building2 className="w-3.5 h-3.5" />
              <span className="truncate">{orgName}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-slate-400">
          {expiresAt && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10">
              <Clock className="w-3 h-3" />
              {expiresIn > 24
                ? `Expires in ${Math.round(expiresIn / 24)}d`
                : `Expires in ${expiresIn}h`}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10">
            <Eye className="w-3 h-3" />
            {meta.view_count} {meta.view_count === 1 ? 'view' : 'views'}
          </span>
        </div>
      </div>

      {/* Artifact content */}
      <Card className="bg-slate-900/60 border-white/10 backdrop-blur-sm">
        <CardContent className="p-6">
          {data ? (
            <ArtifactRenderer artifactType={meta.artifact_type} data={data.data} />
          ) : (
            <div className="text-center py-10 text-slate-400 text-sm">
              <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" />
              Preparing content…
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer trust strip */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
          <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-slate-400">HMAC-signed · cannot be tampered</span>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
          <Hash className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-slate-400">Read-only · no other org data accessible</span>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-slate-400">Auto-expires · revocable instantly</span>
        </div>
      </div>
    </Shell>
  );
};

// ─── Artifact-specific renderer ────────────────────────────────────────────────
const ArtifactRenderer = ({ artifactType, data }) => {
  if (artifactType === 'dashboard') {
    return (
      <div data-testid="shared-dashboard-content">
        <h2 className="text-lg font-bold mb-1">{data.name || 'Dashboard'}</h2>
        {data.description && <p className="text-sm text-slate-400 mb-4">{data.description}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.widgets || []).slice(0, 12).map((w, idx) => (
            <div key={w.id || idx} className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-1">{w.type || 'widget'}</div>
              <div className="text-base font-semibold truncate">{w.title || `Widget ${idx + 1}`}</div>
              {w.value != null && <div className="text-2xl font-bold mt-2 text-emerald-400">{typeof w.value === 'number' ? w.value.toLocaleString() : w.value}</div>}
              {w.description && <div className="text-xs text-slate-400 mt-2 line-clamp-2">{w.description}</div>}
            </div>
          ))}
        </div>
        {!data.widgets?.length && (
          <div className="text-sm text-slate-400 text-center py-8">This dashboard has no widgets.</div>
        )}
      </div>
    );
  }
  if (artifactType === 'focus_export') {
    return (
      <div data-testid="shared-focus-content">
        <h2 className="text-lg font-bold mb-1">FOCUS 1.{data.version?.split('.')[1] || '1'} Export Snapshot</h2>
        <p className="text-sm text-slate-400 mb-4">
          {data.note || 'A point-in-time export following the FinOps Open Cost & Usage Specification.'}
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="text-xs font-bold tracking-widest text-emerald-300 mb-1">VERSION</div>
            <div className="text-2xl font-bold">FOCUS {data.version}</div>
          </div>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-xs font-bold tracking-widest text-slate-400 mb-1">SNAPSHOT ID</div>
            <div className="text-sm font-mono text-slate-300 truncate">{data.snapshot_id}</div>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          Full streaming CSV export available to authenticated members at <code className="bg-white/5 px-1.5 py-0.5 rounded">/api/exports/focus</code>.
        </p>
      </div>
    );
  }
  return (
    <div className="text-sm text-slate-400">
      <p>Unsupported artifact type: <code>{artifactType}</code></p>
    </div>
  );
};

export default SharedViewerPage;
